import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, QrCode, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { REGISTER_URL, SIGN_IN_URL } from '../lib/authLinks';

const Header = () => {
  const MENU_CLOSE_DELAY_MS = 5000;
  const [isScrolled, setIsScrolled] = useState(false);
  const [showTradingMenu, setShowTradingMenu] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileTradingOpen, setMobileTradingOpen] = useState(false);
  const [mobileDownloadOpen, setMobileDownloadOpen] = useState(false);
  const tradingMenuRef = useRef(null);
  const downloadMenuRef = useRef(null);
  const tradingTimeoutRef = useRef(null);
  const downloadTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      clearTradingCloseTimer();
      clearDownloadCloseTimer();
      setShowTradingMenu(false);
      setShowDownloadMenu(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (tradingTimeoutRef.current) clearTimeout(tradingTimeoutRef.current);
      if (downloadTimeoutRef.current) clearTimeout(downloadTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!tradingMenuRef.current?.contains(event.target)) {
        clearTradingCloseTimer();
        setShowTradingMenu(false);
      }

      if (!downloadMenuRef.current?.contains(event.target)) {
        clearDownloadCloseTimer();
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const clearTradingCloseTimer = () => {
    if (tradingTimeoutRef.current) {
      clearTimeout(tradingTimeoutRef.current);
      tradingTimeoutRef.current = null;
    }
  };

  const clearDownloadCloseTimer = () => {
    if (downloadTimeoutRef.current) {
      clearTimeout(downloadTimeoutRef.current);
      downloadTimeoutRef.current = null;
    }
  };

  const scheduleTradingClose = () => {
    clearTradingCloseTimer();
    tradingTimeoutRef.current = setTimeout(() => {
      setShowTradingMenu(false);
      tradingTimeoutRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  };

  const scheduleDownloadClose = () => {
    clearDownloadCloseTimer();
    downloadTimeoutRef.current = setTimeout(() => {
      setShowDownloadMenu(false);
      downloadTimeoutRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  };

  const handleTradingMouseEnter = () => {
    clearTradingCloseTimer();
    clearDownloadCloseTimer();
    setShowDownloadMenu(false);
    setShowTradingMenu(true);
  };

  const handleTradingMouseLeave = (event) => {
    if (tradingMenuRef.current?.contains(event.relatedTarget)) return;
    scheduleTradingClose();
  };

  const handleDownloadMouseEnter = () => {
    clearDownloadCloseTimer();
    clearTradingCloseTimer();
    setShowTradingMenu(false);
    setShowDownloadMenu(true);
  };

  const handleDownloadMouseLeave = (event) => {
    if (downloadMenuRef.current?.contains(event.relatedTarget)) return;
    scheduleDownloadClose();
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled ? 'glass-dark shadow-premium border-b border-white/5 animate-fade-in-down' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center hover-scale transition-all duration-300 group">
          <span className="text-2xl sm:text-3xl font-bold gradient-gold-text whitespace-nowrap">
            PreciseFx
          </span>
        </Link>

        {/* Navigation */}
        <nav className="hidden md:flex items-center space-x-10">
          {/* Trading with Dropdown */}
          <div 
            ref={tradingMenuRef}
            className="relative"
            onMouseEnter={handleTradingMouseEnter}
            onMouseLeave={handleTradingMouseLeave}
          >
            <Link to="/trading" className="text-gray-300 hover:text-white transition-all duration-300 flex items-center font-medium tracking-wide group">
              Trading
              <ChevronDown className="w-4 h-4 ml-1 group-hover:translate-y-0.5 transition-transform duration-300" />
            </Link>
            
            {/* Trading Mega Menu */}
            {showTradingMenu && (
              <div 
                className="absolute top-full left-0 w-screen max-w-4xl pt-4 z-50"
                onMouseEnter={handleTradingMouseEnter}
                onMouseLeave={handleTradingMouseLeave}
              >
                <div className="rounded-3xl border border-white/12 bg-black/90 shadow-premium backdrop-blur-2xl p-10 animate-fade-in-down">
                  {/* Trading Options Grid - 4 columns x 3 rows */}
                  <div className="grid grid-cols-4 gap-5">
                      {/* Row 1 */}
                      <Link to="/trading/how-to-trade" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">How to trade</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Easy steps to start trading</p>
                      </Link>

                      <Link to="/trading/stocks" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Stocks</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Blue chips trading with zero commission</p>
                      </Link>

                      <Link to="/trading/demo-account" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">AI Auto Trading</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Automated trading with advanced AI technology</p>
                      </Link>

                      <Link to="/trading/india" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">PreciseFx for India</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Discover local benefits and advantages</p>
                      </Link>

                      {/* Row 2 */}
                      <Link to="/trading/fixed-time" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">AI Smart Trading</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Automated trading with $100 minimum investment</p>
                      </Link>

                      <Link to="/trading/account" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Account</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Demo and live accounts in currency and crypto</p>
                      </Link>

                      <Link to="/trading/flex" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Flex</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Discover flexible trading mechanics</p>
                      </Link>

                      <Link to="/trading/withdrawals" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Withdrawals</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Withdrawal options, fees, and processing times</p>
                      </Link>

                      {/* Row 3 */}
                      <Link to="/trading/forex" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Forex</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Classic mode for short- and long-term trading</p>
                      </Link>

                      <Link to="/trading/islamic" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Islamic Account</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Halal trading on a swap free account</p>
                      </Link>

                      <Link to="/trading/promotions" onClick={() => setShowTradingMenu(false)} className="rounded-xl border border-white/10 bg-black/70 p-5 transition-all duration-300 hover:border-yellow-500/40 hover:bg-zinc-900/95 hover-lift group">
                        <h3 className="text-base font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Promotions</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">Loyalty program and bonuses</p>
                      </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Download App with Dropdown */}
          <div 
            ref={downloadMenuRef}
            className="relative"
            onMouseEnter={handleDownloadMouseEnter}
            onMouseLeave={handleDownloadMouseLeave}
          >
            <Link to="/download" className="text-gray-300 hover:text-white transition-all duration-300 flex items-center font-medium tracking-wide group">
              Download App
              <ChevronDown className="w-4 h-4 ml-1 group-hover:translate-y-0.5 transition-transform duration-300" />
            </Link>
            
            {/* Download Mega Menu */}
            {showDownloadMenu && (
              <div 
                className="absolute top-full left-0 w-screen max-w-3xl pt-4 z-50"
                onMouseEnter={handleDownloadMouseEnter}
                onMouseLeave={handleDownloadMouseLeave}
              >
                <div className="glass-dark rounded-3xl shadow-premium border border-white/10 p-10 animate-fade-in-down">
                  <div className="grid grid-cols-2 gap-10">
                    {/* Left Side - QR Code */}
                    <div className="flex items-center justify-center gradient-gold rounded-2xl p-10 shadow-gold-lg">
                      <QrCode className="w-48 h-48 text-black" />
                    </div>

                    {/* Right Side - Download Options */}
                    <div className="grid grid-cols-2 gap-5">
                      {/* Android */}
                      <a href="https://admin.spreadex.asia/api/downloads/tenant-apk/MOB_D002FEC3B8" target="_blank" rel="noopener noreferrer" className="glass rounded-xl p-5 hover:glass-gold transition-all duration-300 hover-lift group">
                        <h3 className="text-lg font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Android</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">Instruction to install app directly from our website</p>
                      </a>

                      {/* iOS */}
                      <a href="https://users.spreadex.asia" target="_blank" rel="noopener noreferrer" className="glass rounded-xl p-5 hover:glass-gold transition-all duration-300 hover-lift group">
                        <h3 className="text-lg font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">iOS</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">Trading app is available in multiple stores</p>
                      </a>

                      {/* Web App */}
                      <a href="https://users.spreadex.asia" target="_blank" rel="noopener noreferrer" className="glass rounded-xl p-5 hover:glass-gold transition-all duration-300 hover-lift group">
                        <h3 className="text-lg font-semibold mb-2 text-white group-hover:gradient-gold-text transition-all">Web App</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">Trading from any device via browser, no install required</p>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <Link to="/about" className="text-gray-300 hover:text-white transition-all duration-300 font-medium tracking-wide">
            About
          </Link>
          <Link to="/contact" className="text-gray-300 hover:text-white transition-all duration-300 font-medium tracking-wide">
            Contact Us
          </Link>
          <Link to="/help" className="text-gray-300 hover:text-white transition-all duration-300 font-medium tracking-wide">
            Help
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-white p-2"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Right side actions - Desktop only */}
        <div className="hidden md:flex items-center space-x-6">
          {/* Sign In */}
          <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer">
            <button className="text-gray-300 hover:text-white transition-all duration-300 font-medium tracking-wide">
              Sign in
            </button>
          </a>

          {/* Get started */}
          <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
            <Button className="gradient-gold hover:shadow-gold-lg text-black font-bold px-8 py-3 rounded-xl transition-all duration-300 hover-scale shadow-gold">
              Get Started
            </Button>
          </a>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-black border-t border-gray-800 animate-fade-in-down">
          <div className="px-6 py-4 space-y-4">
            {/* Trading Dropdown */}
            <div>
              <button
                onClick={() => setMobileTradingOpen(!mobileTradingOpen)}
                className="w-full flex items-center justify-between text-gray-300 hover:text-white py-2"
              >
                <span>Trading</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileTradingOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileTradingOpen && (
                <div className="pl-4 space-y-2 mt-2">
                  <Link to="/trading/how-to-trade" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    How to trade
                  </Link>
                  <Link to="/trading/stocks" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Stocks
                  </Link>
                  <Link to="/trading/demo-account" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    AI Auto Trading
                  </Link>
                  <Link to="/trading/india" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    PreciseFx for India
                  </Link>
                  <Link to="/trading/fixed-time" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    AI Smart Trading
                  </Link>
                  <Link to="/trading/account" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Account
                  </Link>
                  <Link to="/trading/flex" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Flex
                  </Link>
                  <Link to="/trading/withdrawals" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Withdrawals
                  </Link>
                  <Link to="/trading/forex" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Forex
                  </Link>
                  <Link to="/trading/islamic" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Islamic Account
                  </Link>
                  <Link to="/trading/promotions" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Promotions
                  </Link>
                </div>
              )}
            </div>

            {/* Download App Dropdown */}
            <div>
              <button
                onClick={() => setMobileDownloadOpen(!mobileDownloadOpen)}
                className="w-full flex items-center justify-between text-gray-300 hover:text-white py-2"
              >
                <span>Download App</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileDownloadOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileDownloadOpen && (
                <div className="pl-4 space-y-2 mt-2">
                  <a href="https://admin.spreadex.asia/api/downloads/tenant-apk/MOB_D002FEC3B8" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Android
                  </a>
                  <a href="https://users.spreadex.asia" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    iOS
                  </a>
                  <a href="https://users.spreadex.asia" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                    Web App
                  </a>
                </div>
              )}
            </div>

            <Link to="/about" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
              About
            </Link>
            <Link to="/contact" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
              Contact Us
            </Link>
            <Link to="/help" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
              Help
            </Link>

            {/* Sign In and Try for Free buttons */}
            <div className="pt-4 border-t border-gray-800 space-y-3">
              <a href={SIGN_IN_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                <button className="w-full text-center text-gray-300 hover:text-white py-3 border border-gray-700 rounded-lg transition-colors">
                  Sign in
                </button>
              </a>
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 rounded-lg">
                  Try for free
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
